import { useState, useEffect, useMemo } from 'react';
import useSwipeTabs from '../hooks/useSwipeTabs';
import {
    TrendingUp, TrendingDown, Loader2,
    Truck, Store, ShoppingBag, MapPin, CloudRain, Sun, Thermometer, Calendar,
    Zap, Flame, Droplets, Timer, Check, Users,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import TabBar from '../components/layout/TabBar';
import { muze, zalozkyModulu } from '../lib/moduly';
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
    ResponsiveContainer, CartesianGrid, Legend, Cell,
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { SKLO_SVETLE } from '../lib/uiTokens';

// ── Barva sekce "Řízení & Zlepšování" ──
const COLOR = '#3B82F6';
const COLOR_BG = 'rgba(59,130,246,0.12)';

// ── Frenštát centrum (default center mapy) ──
const FRENSTAT_CENTER = [49.5483, 18.2108];

const KANAL_COLORS = {
    rozvoz: { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', Icon: Truck,       label: 'Rozvoz' },
    bistro: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', Icon: Store,       label: 'Bistro' },
    vydej:  { color: '#10b981', bg: 'rgba(16,185,129,0.12)', Icon: ShoppingBag, label: 'Výdej' },
};

const DNY_LABELS = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

const RANGES = [
    { label: '7D',      days: 7 },
    { label: '30D',     days: 30 },
    { label: '90D',     days: 90 },
    { label: 'YTD',     days: null },
    { label: 'Vše',     days: -1 },
    { label: 'Vlastní', days: null },
];

const ENERGIE_COLORS = {
    elektrina: { color: '#0CC0DF', bg: 'rgba(12,192,223,0.12)', Icon: Zap,      label: 'Elektřina' },
    plyn:      { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', Icon: Flame,    label: 'Plyn' },
    voda:      { color: '#3B82F6', bg: 'rgba(59,130,246,0.12)', Icon: Droplets, label: 'Voda' },
};

const fmt = (v) => {
    if (v == null) return '—';
    return new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(v);
};

const fmtKc = (v) => v != null ? `${fmt(v)} Kč` : '—';

function computeDateRange(rangeIdx, vlastniOd, vlastniDo) {
    const doo = new Date();
    const doStr = doo.toISOString().slice(0, 10);

    if (rangeIdx === 5) return { od: vlastniOd, do: vlastniDo || doStr };
    if (rangeIdx === 4) return { od: '2025-01-01', do: doStr };
    if (rangeIdx === 3) return { od: `${doo.getFullYear()}-01-01`, do: doStr };
    const r = RANGES[rangeIdx];
    const od = new Date(doo);
    od.setDate(od.getDate() - r.days + 1);
    return { od: od.toISOString().slice(0, 10), do: doStr };
}

// ── Heat mapa buňka ──
function HeatCell({ value, max, isDark, tooltip }) {
    const intensity = max > 0 ? value / max : 0;
    const bg = isDark
        ? `rgba(59,130,246,${(intensity * 0.7 + 0.05).toFixed(2)})`
        : `rgba(59,130,246,${(intensity * 0.6 + 0.04).toFixed(2)})`;
    const textColor = intensity > 0.5 ? '#fff' : 'var(--text-secondary)';

    return (
        <div
            className="flex items-center justify-center rounded text-xs font-medium"
            style={{ background: bg, color: textColor, minHeight: 32 }}
            title={tooltip}
        >
            {value > 0 ? value : ''}
        </div>
    );
}

export default function Dashboard({ user, initialTab = null }) {
    const { resolvedTheme } = useTheme();
    const isDark = resolvedTheme === 'dark';

    // Záložky kreslí TabBar z katalogu; tady jsou jen kvůli přejetí prstem.
    const tabs = zalozkyModulu('dashboard', user);
    // Záložka z adresy (#/dashboard?tab=energie) — proklik z měřidel v Kontrolách a měření
    const [activeTab, setActiveTab] = useState(() => (tabs.some(t => t.id === initialTab) ? initialTab : null));
    const swipeProps = useSwipeTabs(tabs.map(t => t.id), activeTab, setActiveTab);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);

    const [rangeIdx, setRangeIdx] = useState(1);
    const [vlastniOd, setVlastniOd] = useState('');
    const [vlastniDo, setVlastniDo] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [heatKanal, setHeatKanal] = useState('vse');
    const [energieData, setEnergieData] = useState(null);
    const [energieLoading, setEnergieLoading] = useState(false);
    const [doruceniData, setDoruceniData] = useState(null);
    const [doruceniLoading, setDoruceniLoading] = useState(false);
    const [kvalitaData, setKvalitaData] = useState(null);
    const [kvalitaLoading, setKvalitaLoading] = useState(false);

    const cardStyle = isDark ? {
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.10)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
    } : SKLO_SVETLE;

    const tooltipStyle = {
        background: isDark ? 'rgba(30,30,40,0.95)' : '#fff',
        border: '1px solid var(--border-default)',
        borderRadius: 6,
        fontSize: 12,
        color: 'var(--text-primary)',
    };

    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.08)' : 'var(--surface-elevated)',
        border: '1px solid var(--border-default)',
        borderRadius: 6,
        padding: '6px 10px',
        fontSize: 13,
        color: 'var(--text-primary)',
    };

    // ── Fetch tržby ──
    useEffect(() => {
        if (activeTab !== 'trzby') return;
        const { od, do: doo } = computeDateRange(rangeIdx, vlastniOd, vlastniDo);
        if (!od || !doo) return;
        setLoading(true);
        fetch(`/api/kpi/trzby?od=${od}&do=${doo}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { setData(d); setLoading(false); })
            .catch(() => setLoading(false));
    }, [activeTab, rangeIdx, vlastniOd, vlastniDo]);

    // ── Fetch energie ──
    useEffect(() => {
        if (activeTab !== 'energie') return;
        const { od, do: doo } = computeDateRange(rangeIdx, vlastniOd, vlastniDo);
        if (!od || !doo) return;
        setEnergieLoading(true);
        fetch(`/api/kpi/energie?od=${od}&do=${doo}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { setEnergieData(d); setEnergieLoading(false); })
            .catch(() => setEnergieLoading(false));
    }, [activeTab, rangeIdx, vlastniOd, vlastniDo]);

    // ── Fetch doručení ──
    useEffect(() => {
        if (activeTab !== 'doruceni') return;
        const { od, do: doo } = computeDateRange(rangeIdx, vlastniOd, vlastniDo);
        if (!od || !doo) return;
        setDoruceniLoading(true);
        fetch(`/api/kpi/doruceni?od=${od}&do=${doo}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { setDoruceniData(d); setDoruceniLoading(false); })
            .catch(() => setDoruceniLoading(false));
    }, [activeTab, rangeIdx, vlastniOd, vlastniDo]);

    // ── Fetch kvalita (Kontroly a měření) ──
    useEffect(() => {
        if (activeTab !== 'kvalita') return;
        const { od, do: doo } = computeDateRange(rangeIdx, vlastniOd, vlastniDo);
        if (!od || !doo) return;
        setKvalitaLoading(true);
        fetch(`/api/kpi/kvalita?od=${od}&do=${doo}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { setKvalitaData(d); setKvalitaLoading(false); })
            .catch(() => setKvalitaLoading(false));
    }, [activeTab, rangeIdx, vlastniOd, vlastniDo]);

    // ── Počasí korelace ──
    const pocasiStats = useMemo(() => {
        if (!data?.denni?.length) return null;
        const dny = data.denni.filter(d => d.pocet > 0);

        const dest = dny.filter(d => Number(d.srazky_mm) > 1);
        const sucho = dny.filter(d => d.srazky_mm != null && Number(d.srazky_mm) <= 1);
        const avgDest = dest.length ? Math.round(dest.reduce((s, d) => s + Number(d.trzby_s_dph), 0) / dest.length) : 0;
        const avgSucho = sucho.length ? Math.round(sucho.reduce((s, d) => s + Number(d.trzby_s_dph), 0) / sucho.length) : 0;

        const pasma = [
            { label: '< 5 °C', min: -99, max: 5, dny: [] },
            { label: '5–15 °C', min: 5, max: 15, dny: [] },
            { label: '15–25 °C', min: 15, max: 25, dny: [] },
            { label: '> 25 °C', min: 25, max: 99, dny: [] },
        ];
        for (const d of dny) {
            if (d.teplota_prumer == null) continue;
            const t = Number(d.teplota_prumer);
            const p = pasma.find(p => t >= p.min && t < p.max);
            if (p) p.dny.push(d);
        }
        const teplotni = pasma.map(p => ({
            label: p.label,
            pocet_dni: p.dny.length,
            prumer: p.dny.length ? Math.round(p.dny.reduce((s, d) => s + Number(d.trzby_s_dph), 0) / p.dny.length) : 0,
        }));

        return { dest: { pocet: dest.length, prumer: avgDest }, sucho: { pocet: sucho.length, prumer: avgSucho }, teplotni };
    }, [data]);

    // ── Heat mapa ──
    const heatData = useMemo(() => {
        if (!data?.heat_mapa?.length) return { matrix: {}, max: 0, hodiny: [] };
        const filtered = heatKanal === 'vse' ? data.heat_mapa : data.heat_mapa.filter(r => r.kanal === heatKanal);
        const matrix = {};
        let max = 0;
        const hodinySet = new Set();
        for (const r of filtered) {
            if (!matrix[r.den]) matrix[r.den] = {};
            const existing = matrix[r.den][r.hodina];
            if (existing) {
                existing.pocet += r.pocet;
                existing.trzby += r.trzby;
            } else {
                matrix[r.den][r.hodina] = { pocet: r.pocet, trzby: r.trzby };
            }
            hodinySet.add(r.hodina);
        }
        for (const den of Object.values(matrix)) {
            for (const cell of Object.values(den)) {
                if (cell.pocet > max) max = cell.pocet;
            }
        }
        const hodiny = [...hodinySet].sort((a, b) => a - b);
        return { matrix, max, hodiny };
    }, [data, heatKanal]);

    // ── Chart data ──
    const chartData = useMemo(() => {
        if (!data?.denni) return [];
        return data.denni.map(d => ({
            ...d,
            trzby_s_dph: Number(d.trzby_s_dph),
            teplota_prumer: d.teplota_prumer != null ? Number(d.teplota_prumer) : null,
            srazky_mm: d.srazky_mm != null ? Number(d.srazky_mm) : null,
            label: new Date(d.datum).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' }),
        }));
    }, [data]);

    return (
        <div style={{
            display: 'flex', flexDirection: 'column',
            height: '100%', minHeight: 0,
            padding: isMobile ? '12px 12px 0' : '24px 24px 0',
            overflow: 'hidden',
        }} {...swipeProps}>
            {/* Header — na mobilu se zobrazuje v TopBaru, tady skryjeme */}
            {!isMobile && (
                <div className="flex items-center gap-3" style={{ flexShrink: 0, marginBottom: 20 }}>
                    <div className="flex items-center justify-center" style={{
                        width: 40, height: 40,
                        borderRadius: 9, background: COLOR_BG, flexShrink: 0,
                    }}>
                        <TrendingUp size={20} color={COLOR} />
                    </div>
                    <div>
                        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0, lineHeight: 1.2 }}>KPI Dashboard</h1>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)', margin: 0 }}>Klíčové ukazatele výkonnosti</p>
                    </div>
                </div>
            )}

            <div style={{ flexShrink: 0, marginBottom: isMobile ? 10 : 20 }}>
                <TabBar modul="dashboard" user={user} aktivni={activeTab} onZmena={setActiveTab}
                    barva={COLOR} isDark={isDark} isMobile={isMobile} />
            </div>

            {/* ── Scrollovatelný obsah ── */}
            <div style={{
                flex: '1 1 0', minHeight: 0,
                overflowY: 'auto', overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch',
                paddingBottom: isMobile ? 12 : 24,
            }}>
                {/* Date range — sdílený pro všechny taby */}
                <div style={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                    gap: isMobile ? 6 : 8,
                    marginBottom: isMobile ? 12 : 20,
                }}>
                    {RANGES.map((r, i) => (
                        <button
                            key={r.label}
                            onClick={() => setRangeIdx(i)}
                            style={{
                                padding: isMobile ? '4px 10px' : '6px 12px',
                                borderRadius: 9999, fontSize: isMobile ? 11 : 12, fontWeight: 500,
                                cursor: 'pointer', border: `1px solid ${rangeIdx === i ? COLOR : 'var(--border-default)'}`,
                                background: rangeIdx === i ? COLOR : 'transparent',
                                color: rangeIdx === i ? '#fff' : 'var(--text-secondary)',
                                transition: 'all 0.15s ease',
                            }}
                        >
                            {r.label}
                        </button>
                    ))}
                    {rangeIdx === 5 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: isMobile ? '1 1 100%' : 'none' }}>
                            <input type="date" value={vlastniOd} onChange={e => setVlastniOd(e.target.value)}
                                style={{ ...inputStyle, flex: isMobile ? 1 : 'none', minWidth: 0, padding: isMobile ? '5px 8px' : '6px 10px' }} />
                            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>–</span>
                            <input type="date" value={vlastniDo} onChange={e => setVlastniDo(e.target.value)}
                                style={{ ...inputStyle, flex: isMobile ? 1 : 'none', minWidth: 0, padding: isMobile ? '5px 8px' : '6px 10px' }} />
                        </div>
                    )}
                </div>

                {activeTab === 'trzby' && (
                    loading ? (
                        <div className="flex items-center justify-center" style={{ padding: '60px 0' }}>
                            <Loader2 size={24} className="animate-spin" style={{ color: COLOR }} />
                        </div>
                    ) : !data ? (
                        <div className="text-center" style={{ padding: '40px 0', color: 'var(--text-tertiary)' }}>Nepodařilo se načíst data</div>
                    ) : (
                        <TrzbyContent
                            data={data} chartData={chartData} pocasiStats={pocasiStats}
                            heatData={heatData} heatKanal={heatKanal} setHeatKanal={setHeatKanal}
                            cardStyle={cardStyle} tooltipStyle={tooltipStyle} isDark={isDark} isMobile={isMobile}
                        />
                    )
                )}

                {activeTab === 'energie' && (
                    energieLoading ? (
                        <div className="flex items-center justify-center" style={{ padding: '60px 0' }}>
                            <Loader2 size={24} className="animate-spin" style={{ color: COLOR }} />
                        </div>
                    ) : !energieData ? (
                        <div className="text-center" style={{ padding: '40px 0', color: 'var(--text-tertiary)' }}>Nepodařilo se načíst data</div>
                    ) : (
                        <EnergieContent data={energieData} cardStyle={cardStyle} tooltipStyle={tooltipStyle} isDark={isDark} isMobile={isMobile} />
                    )
                )}

                {activeTab === 'kvalita' && (
                    kvalitaLoading ? (
                        <div className="flex items-center justify-center" style={{ padding: '60px 0' }}>
                            <Loader2 size={24} className="animate-spin" style={{ color: COLOR }} />
                        </div>
                    ) : !kvalitaData ? (
                        <div className="text-center" style={{ padding: '40px 0', color: 'var(--text-tertiary)' }}>Nepodařilo se načíst data</div>
                    ) : (
                        <KvalitaContent data={kvalitaData} cardStyle={cardStyle} tooltipStyle={tooltipStyle} isDark={isDark} isMobile={isMobile} />
                    )
                )}

                {activeTab === 'doruceni' && (
                    doruceniLoading ? (
                        <div className="flex items-center justify-center" style={{ padding: '60px 0' }}>
                            <Loader2 size={24} className="animate-spin" style={{ color: COLOR }} />
                        </div>
                    ) : !doruceniData ? (
                        <div className="text-center" style={{ padding: '40px 0', color: 'var(--text-tertiary)' }}>Nepodařilo se načíst data</div>
                    ) : (
                        <DoruceniContent data={doruceniData} cardStyle={cardStyle} tooltipStyle={tooltipStyle} isDark={isDark} isMobile={isMobile} />
                    )
                )}
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// Obsah tabu Tržby
// ════════════════════════════════════════════════════════════════════════════

function TrzbyContent({ data, chartData, pocasiStats, heatData, heatKanal, setHeatKanal, cardStyle, tooltipStyle, isDark, isMobile }) {
    const { souhrn, po_kanalech, po_dnech, mesicni } = data;

    const kpi = [
        { label: 'Tržby celkem',        value: fmtKc(souhrn.trzby_celkem),       color: COLOR },
        { label: 'Počet objednávek',     value: fmt(souhrn.pocet_celkem),         color: '#3b82f6' },
        { label: 'Ø objednávka',         value: fmtKc(souhrn.prumer_objednavka),  color: '#10b981' },
        {
            label: 'Trend vs předchozí',
            value: souhrn.zmena_pct != null ? `${souhrn.zmena_pct > 0 ? '+' : ''}${souhrn.zmena_pct} %` : '—',
            color: souhrn.zmena_pct >= 0 ? '#10b981' : '#ef4444',
            icon: souhrn.zmena_pct >= 0 ? TrendingUp : TrendingDown,
        },
    ];

    return (
        <div className="space-y-6">
            {/* A. KPI karty */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {kpi.map(k => (
                    <div key={k.label} className="rounded-xl p-4" style={cardStyle}>
                        <div className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>{k.label}</div>
                        <div className="flex items-center gap-1.5">
                            <span className="text-lg font-bold" style={{ color: k.color }}>{k.value}</span>
                            {k.icon && <k.icon size={16} style={{ color: k.color }} />}
                        </div>
                    </div>
                ))}
            </div>

            {/* B. Denní graf */}
            <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 12 : 16 }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Denní tržby</h3>
                <div style={{ height: isMobile ? 200 : 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="trzbyGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={COLOR} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={COLOR} stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} stroke="var(--border-default)" interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} tickFormatter={v => `${Math.round(v / 1000)}k`} stroke="var(--border-default)" width={45} />
                            <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmtKc(v), 'Tržby']} labelFormatter={l => l} />
                            <Area type="monotone" dataKey="trzby_s_dph" stroke={COLOR} strokeWidth={2} fill="url(#trzbyGrad)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* C. Kanály */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {Object.entries(KANAL_COLORS).map(([k, cfg]) => {
                    const d = po_kanalech[k];
                    if (!d) return null;
                    const pct = souhrn.trzby_celkem > 0 ? ((d.trzby / souhrn.trzby_celkem) * 100).toFixed(1) : 0;
                    const avg = d.pocet > 0 ? Math.round(d.trzby / d.pocet) : 0;
                    return (
                        <div key={k} className="rounded-xl p-4" style={cardStyle}>
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: cfg.bg }}>
                                    <cfg.Icon size={16} color={cfg.color} />
                                </div>
                                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{cfg.label}</span>
                                <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: cfg.bg, color: cfg.color }}>{pct} %</span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div>
                                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Tržby</div>
                                    <div className="text-sm font-bold" style={{ color: cfg.color }}>{fmtKc(d.trzby)}</div>
                                </div>
                                <div>
                                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Objednávky</div>
                                    <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{fmt(d.pocet)}</div>
                                </div>
                                <div>
                                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Ø obj</div>
                                    <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{fmtKc(avg)}</div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* D. Heat mapa */}
            {heatData.hodiny.length > 0 && (
                <div className="rounded-xl p-4" style={cardStyle}>
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Objednávky dle dne a hodiny</h3>
                        <div className="flex gap-1">
                            {[['vse', 'Vše'], ['rozvoz', 'Rozvoz'], ['bistro', 'Bistro'], ['vydej', 'Výdej']].map(([val, label]) => (
                                <button
                                    key={val}
                                    onClick={() => setHeatKanal(val)}
                                    className="px-2 py-0.5 rounded text-xs font-medium transition-colors"
                                    style={{
                                        background: heatKanal === val ? 'var(--accent-primary)' : 'var(--surface-secondary)',
                                        color: heatKanal === val ? '#fff' : 'var(--text-secondary)',
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <div style={{ display: 'grid', gridTemplateColumns: `48px repeat(${heatData.hodiny.length}, 1fr)`, gap: 2, minWidth: heatData.hodiny.length * 48 + 48 }}>
                            <div />
                            {heatData.hodiny.map(h => (
                                <div key={h} className="text-center text-xs font-medium py-1" style={{ color: 'var(--text-secondary)' }}>
                                    {h}h
                                </div>
                            ))}
                            {DNY_LABELS.map((den, di) => (
                                <div key={di} style={{ display: 'contents' }}>
                                    <div className="flex items-center text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                                        {den}
                                    </div>
                                    {heatData.hodiny.map(h => {
                                        const cell = heatData.matrix[di]?.[h];
                                        return (
                                            <HeatCell
                                                key={`${di}-${h}`}
                                                value={cell?.pocet || 0}
                                                max={heatData.max}
                                                isDark={isDark}
                                                tooltip={cell ? `${den} ${h}:00 — ${cell.pocet} obj, ${fmtKc(cell.trzby)}` : `${den} ${h}:00`}
                                            />
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* E. Den v týdnu */}
            {po_dnech?.length > 0 && (
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 12 : 16 }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Průměrné tržby dle dne v týdnu</h3>
                    <div style={{ height: isMobile ? 180 : 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={po_dnech} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} />
                                <XAxis dataKey="den" tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} stroke="var(--border-default)" />
                                <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} tickFormatter={v => `${Math.round(v / 1000)}k`} stroke="var(--border-default)" width={40} />
                                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmtKc(v), 'Ø tržby/den']} labelFormatter={l => l} />
                                <Bar dataKey="prumer_trzby" radius={[4, 4, 0, 0]} fill={COLOR} fillOpacity={0.85} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* F. Top lokality — mapa + tabulka vedle sebe */}
            {(data.po_mestech?.length > 0 || data.mapa_zakazniku?.length > 0) && (
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 12 : 16 }}>
                    <div className="flex items-center gap-2 mb-3">
                        <MapPin size={16} color={COLOR} />
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Odkud objednávají</h3>
                        {data.mapa_zakazniku?.length > 0 && (
                            <span className="text-xs ml-auto" style={{ color: 'var(--text-muted)' }}>
                                {data.mapa_zakazniku.length} adres
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 16 }}>
                        {/* Mapa */}
                        {data.mapa_zakazniku?.length > 0 ? (
                            <div className="flex-1 rounded-xl overflow-hidden" style={{ height: isMobile ? 260 : 400, minWidth: 0 }}>
                                <MapContainer
                                    center={FRENSTAT_CENTER}
                                    zoom={13}
                                    style={{ height: '100%', width: '100%' }}
                                    scrollWheelZoom={true}
                                >
                                    <TileLayer
                                        url={isDark
                                            ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                                            : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
                                        attribution='&copy; OSM'
                                    />
                                    {data.mapa_zakazniku.map((z, i) => {
                                        const maxUtrata = Number(data.mapa_zakazniku[0]?.celkem_utraceno) || 1;
                                        const ratio = Number(z.celkem_utraceno) / maxUtrata;
                                        const radius = Math.max(3, Math.min(14, ratio * 14));
                                        return (
                                            <CircleMarker
                                                key={i}
                                                center={[Number(z.lat), Number(z.lng)]}
                                                radius={radius}
                                                pathOptions={{
                                                    color: COLOR,
                                                    fillColor: COLOR,
                                                    fillOpacity: 0.3 + ratio * 0.5,
                                                    weight: 1,
                                                }}
                                            >
                                                <Popup>
                                                    <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                                                        <strong>{z.adresa}</strong><br />
                                                        {z.mesto}<br />
                                                        {z.pocet_zakazniku > 1 ? `${z.pocet_zakazniku} zákazníků · ` : ''}{z.pocet_objednavek}× objednáno<br />
                                                        <span style={{ color: COLOR, fontWeight: 600 }}>{fmtKc(z.celkem_utraceno)}</span>
                                                    </div>
                                                </Popup>
                                            </CircleMarker>
                                        );
                                    })}
                                </MapContainer>
                            </div>
                        ) : (
                            <div className="flex-1 rounded-xl flex items-center justify-center" style={{ height: isMobile ? 200 : 400, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                                <div className="text-center">
                                    <MapPin size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} />
                                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Mapa se načítá — geocoding adres probíhá</p>
                                </div>
                            </div>
                        )}

                        {/* Tabulka měst */}
                        {data.po_mestech?.length > 0 && (
                            <div style={{ width: isMobile ? '100%' : undefined, maxHeight: isMobile ? 200 : 400, overflowY: 'auto' }} className={isMobile ? '' : 'lg:w-80 xl:w-96'}>
                                <table className="w-full" style={{ fontSize: isMobile ? 12 : 14 }}>
                                    <thead className="sticky top-0" style={{ background: isDark ? '#141416' : 'var(--surface-raised)' }}>
                                        <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                            <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--text-secondary)' }}>#</th>
                                            <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--text-secondary)' }}>Město</th>
                                            <th className="text-right py-2 px-2 font-medium" style={{ color: 'var(--text-secondary)' }}>Zák.</th>
                                            <th className="text-right py-2 px-2 font-medium" style={{ color: 'var(--text-secondary)' }}>Tržby</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.po_mestech.map((m, i) => (
                                            <tr key={m.mesto} style={{
                                                borderBottom: '1px solid var(--border-subtle)',
                                                background: i % 2 === 0 ? 'transparent' : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)'),
                                            }}>
                                                <td className="py-1.5 px-2 font-medium" style={{ color: 'var(--text-muted)' }}>{i + 1}</td>
                                                <td className="py-1.5 px-2 font-medium" style={{ color: 'var(--text-primary)' }}>{m.mesto}</td>
                                                <td className="py-1.5 px-2 text-right" style={{ color: 'var(--text-secondary)' }}>{fmt(m.pocet_zakazniku)}</td>
                                                <td className="py-1.5 px-2 text-right font-medium" style={{ color: COLOR }}>{fmtKc(m.trzby)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* G. Počasí */}
            {pocasiStats && (
                <div className="rounded-xl p-4" style={cardStyle}>
                    <div className="flex items-center gap-2 mb-3">
                        <Thermometer size={16} color={COLOR} />
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Vliv počasí na tržby</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                        <div className="rounded-lg p-3 flex items-center gap-3" style={{ background: isDark ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.06)' }}>
                            <CloudRain size={20} color="#3b82f6" />
                            <div>
                                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Déšť (&gt;1 mm) — {pocasiStats.dest.pocet} dní</div>
                                <div className="text-sm font-bold" style={{ color: '#3b82f6' }}>{fmtKc(pocasiStats.dest.prumer)} / den</div>
                            </div>
                        </div>
                        <div className="rounded-lg p-3 flex items-center gap-3" style={{ background: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.06)' }}>
                            <Sun size={20} color="#f59e0b" />
                            <div>
                                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Sucho (≤1 mm) — {pocasiStats.sucho.pocet} dní</div>
                                <div className="text-sm font-bold" style={{ color: '#f59e0b' }}>{fmtKc(pocasiStats.sucho.prumer)} / den</div>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {pocasiStats.teplotni.map(t => (
                            <div key={t.label} className="rounded-lg p-3 text-center" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' }}>
                                <div className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>{t.label}</div>
                                <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{fmtKc(t.prumer)}</div>
                                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{t.pocet_dni} dní</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* H. Měsíční trend */}
            {mesicni?.length > 1 && (
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 12 : 16 }}>
                    <div className="flex items-center gap-2 mb-3">
                        <Calendar size={16} color={COLOR} />
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Měsíční trend</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                    <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--text-secondary)' }}>Měsíc</th>
                                    <th className="text-right py-2 px-2 font-medium" style={{ color: 'var(--text-secondary)' }}>Tržby</th>
                                    <th className="text-right py-2 px-2 font-medium" style={{ color: 'var(--text-secondary)' }}>Obj</th>
                                    <th className="text-right py-2 px-2 font-medium" style={{ color: 'var(--text-secondary)' }}>Ø obj</th>
                                    <th className="text-right py-2 px-2 font-medium" style={{ color: 'var(--text-secondary)' }}>Změna m/m</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mesicni.map((m, i) => {
                                    const prev = i > 0 ? mesicni[i - 1] : null;
                                    const zmena = prev && prev.trzby > 0
                                        ? (((m.trzby - prev.trzby) / prev.trzby) * 100).toFixed(1)
                                        : null;
                                    const nazvy = ['', 'Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čer', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'];
                                    const [y, mo] = m.mesic.split('-');
                                    const mesicLabel = `${nazvy[parseInt(mo)]} ${y}`;
                                    return (
                                        <tr key={m.mesic} style={{
                                            borderBottom: '1px solid var(--border-subtle)',
                                            background: i % 2 === 0 ? 'transparent' : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)'),
                                        }}>
                                            <td className="py-2 px-2 font-medium" style={{ color: 'var(--text-primary)' }}>{mesicLabel}</td>
                                            <td className="py-2 px-2 text-right font-medium" style={{ color: COLOR }}>{fmtKc(m.trzby)}</td>
                                            <td className="py-2 px-2 text-right" style={{ color: 'var(--text-secondary)' }}>{fmt(m.pocet)}</td>
                                            <td className="py-2 px-2 text-right" style={{ color: 'var(--text-secondary)' }}>{fmtKc(m.prumer)}</td>
                                            <td className="py-2 px-2 text-right font-medium" style={{
                                                color: zmena == null ? 'var(--text-muted)' : zmena >= 0 ? '#10b981' : '#ef4444',
                                            }}>
                                                {zmena != null ? `${zmena > 0 ? '+' : ''}${zmena} %` : '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// Obsah tabu Energie
// ════════════════════════════════════════════════════════════════════════════

function EnergieContent({ data, cardStyle, tooltipStyle, isDark, isMobile }) {
    const { elektrina, voda, plyn, celkem_kc, trzby_obdobi, energie_pct_trzeb } = data;

    // Kombinovaný graf — spojit denní data všech energií do jednoho datasetu
    const kombinovanyGraf = useMemo(() => {
        const byDatum = {};
        (elektrina?.denni || []).forEach(d => {
            byDatum[d.datum] = { ...byDatum[d.datum], datum: d.datum, elektrina: Number(d.kc) };
        });
        (voda?.denni || []).forEach(d => {
            byDatum[d.datum] = { ...byDatum[d.datum], datum: d.datum, voda: Number(d.kc) };
        });
        (plyn?.denni || []).forEach(d => {
            byDatum[d.datum] = { ...byDatum[d.datum], datum: d.datum, plyn: Number(d.kc) };
        });
        return Object.values(byDatum)
            .sort((a, b) => a.datum.localeCompare(b.datum))
            .map(d => ({
                ...d,
                label: new Date(d.datum).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' }),
            }));
    }, [elektrina, voda, plyn]);

    // Elektřina chart data
    const elekChart = useMemo(() => {
        if (!elektrina?.denni?.length) return [];
        return elektrina.denni.map(d => ({
            ...d,
            kwh: Number(d.kwh),
            mimo_provoz_kwh: Number(d.mimo_provoz_kwh),
            label: new Date(d.datum).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' }),
        }));
    }, [elektrina]);

    const elekPctTrzeb = trzby_obdobi > 0 ? ((elektrina?.celkem_kc || 0) / trzby_obdobi * 100).toFixed(1) : '—';
    const vodaPctTrzeb = trzby_obdobi > 0 ? ((voda?.celkem_kc || 0) / trzby_obdobi * 100).toFixed(1) : '—';
    const plynPctTrzeb = trzby_obdobi > 0 ? ((plyn?.celkem_kc || 0) / trzby_obdobi * 100).toFixed(1) : '—';

    return (
        <div className="space-y-6">
            {/* A. KPI karty */}
            <div style={{
                display: 'grid', gap: isMobile ? 8 : 12,
                gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)',
            }}>
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 10 : 16 }}>
                    <div className="flex items-center gap-1.5 mb-1">
                        <Zap size={14} color="#0CC0DF" />
                        <span style={{ fontSize: isMobile ? 10 : 12, color: 'var(--text-secondary)' }}>Elektřina Ø/den</span>
                    </div>
                    <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 700, color: '#0CC0DF' }}>{elektrina?.prumer_kwh_den ?? '—'} kWh</div>
                    <div style={{ fontSize: isMobile ? 10 : 12, color: 'var(--text-muted)' }}>{fmtKc(elektrina?.prumer_kc_den)} · {elekPctTrzeb}%</div>
                </div>
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 10 : 16 }}>
                    <div className="flex items-center gap-1.5 mb-1">
                        <Droplets size={14} color="#3B82F6" />
                        <span style={{ fontSize: isMobile ? 10 : 12, color: 'var(--text-secondary)' }}>Voda Ø/den</span>
                    </div>
                    <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 700, color: '#3B82F6' }}>
                        {voda?.prumer_m3_den ? `${voda.prumer_m3_den} m³` : '—'}
                    </div>
                    <div style={{ fontSize: isMobile ? 10 : 12, color: 'var(--text-muted)' }}>
                        {voda?.celkem_kc ? `${fmtKc(Math.round(voda.celkem_kc / (voda.denni?.length || 1)))} · ${vodaPctTrzeb}%` : 'Žádná data'}
                    </div>
                </div>
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 10 : 16 }}>
                    <div className="flex items-center gap-1.5 mb-1">
                        <Flame size={14} color="#F59E0B" />
                        <span style={{ fontSize: isMobile ? 10 : 12, color: 'var(--text-secondary)' }}>Plyn Ø/den</span>
                    </div>
                    <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 700, color: '#F59E0B' }}>
                        {plyn?.prumer_m3_den ? `${plyn.prumer_m3_den} m³` : '—'}
                    </div>
                    <div style={{ fontSize: isMobile ? 10 : 12, color: 'var(--text-muted)' }}>
                        {plyn?.celkem_kc ? `${fmtKc(Math.round(plyn.celkem_kc / (plyn.denni?.length || 1)))} · ${plynPctTrzeb}%` : 'Žádná data'}
                    </div>
                </div>
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 10 : 16 }}>
                    <div style={{ fontSize: isMobile ? 10 : 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Energie celkem</div>
                    <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 700, color: 'var(--text-primary)' }}>{fmtKc(celkem_kc)}</div>
                    <div style={{ fontSize: isMobile ? 10 : 12, color: 'var(--text-muted)' }}>za období</div>
                </div>
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 10 : 16 }}>
                    <div style={{ fontSize: isMobile ? 10 : 12, color: 'var(--text-secondary)', marginBottom: 4 }}>% z tržeb</div>
                    <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 700, color: energie_pct_trzeb > 5 ? '#ef4444' : energie_pct_trzeb > 3 ? '#f59e0b' : '#10b981' }}>
                        {energie_pct_trzeb != null ? `${energie_pct_trzeb} %` : '—'}
                    </div>
                    <div style={{ fontSize: isMobile ? 10 : 12, color: 'var(--text-muted)' }}>{fmtKc(trzby_obdobi)}</div>
                </div>
            </div>

            {/* B. Kombinovaný stacked graf — denní náklady */}
            {kombinovanyGraf.length > 0 && (
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 12 : 16 }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Denní náklady na energie</h3>
                    <div style={{ height: isMobile ? 200 : 260 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={kombinovanyGraf} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} stroke="var(--border-default)" interval="preserveStartEnd" />
                                <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} tickFormatter={v => `${v} Kč`} stroke="var(--border-default)" width={55} />
                                <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [fmtKc(v), name]} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="elektrina" name="Elektřina" stackId="a" fill="#0CC0DF" fillOpacity={0.85} radius={[0, 0, 0, 0]} />
                                <Bar dataKey="voda" name="Voda" stackId="a" fill="#3B82F6" fillOpacity={0.85} />
                                <Bar dataKey="plyn" name="Plyn" stackId="a" fill="#F59E0B" fillOpacity={0.85} radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* C. Elektřina detail */}
            {elekChart.length > 0 && (
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 12 : 16 }}>
                    <div className="flex items-center gap-2 mb-1">
                        <Zap size={16} color="#0CC0DF" />
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Elektřina — detail</h3>
                    </div>
                    <div className="flex flex-wrap gap-4 mb-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                        <span>Celkem <strong style={{ color: '#0CC0DF' }}>{elektrina.celkem_kwh} kWh</strong></span>
                        <span>Náklady <strong style={{ color: '#0CC0DF' }}>{fmtKc(elektrina.celkem_kc)}</strong></span>
                        <span>Peak <strong>{elektrina.peak_kw} kW</strong></span>
                        <span>Mimo provoz <strong>{elektrina.mimo_provoz_kwh} kWh ({elektrina.mimo_provoz_pct} %)</strong></span>
                    </div>
                    <div style={{ height: isMobile ? 180 : 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={elekChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="elekGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#0CC0DF" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#0CC0DF" stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} stroke="var(--border-default)" interval="preserveStartEnd" />
                                <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} tickFormatter={v => `${v} kWh`} stroke="var(--border-default)" width={55} />
                                <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => {
                                    if (name === 'kwh') return [`${v} kWh`, 'Spotřeba'];
                                    if (name === 'mimo_provoz_kwh') return [`${v} kWh`, 'Mimo provoz'];
                                    return [v, name];
                                }} labelFormatter={l => l} />
                                <Area type="monotone" dataKey="kwh" stroke="#0CC0DF" strokeWidth={2} fill="url(#elekGrad)" />
                                <Area type="monotone" dataKey="mimo_provoz_kwh" stroke="#ef4444" strokeWidth={1} strokeDasharray="4 2" fill="none" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* D. Voda + Plyn */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Voda */}
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 12 : 16 }}>
                    <div className="flex items-center gap-2 mb-2">
                        <Droplets size={16} color="#3B82F6" />
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Voda</h3>
                    </div>
                    {voda?.denni?.length > 0 ? (
                        <>
                            <div className="flex flex-wrap gap-3 mb-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                                <span>Celkem <strong style={{ color: '#3B82F6' }}>{voda.celkem_m3} m³</strong></span>
                                <span>Náklady <strong style={{ color: '#3B82F6' }}>{fmtKc(voda.celkem_kc)}</strong></span>
                                <span>{vodaPctTrzeb} % tržeb</span>
                            </div>
                            <div style={{ height: 160 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={voda.denni.map(d => ({ ...d, m3: Number(d.m3), label: new Date(d.datum).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' }) }))} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} />
                                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} stroke="var(--border-default)" interval="preserveStartEnd" />
                                        <YAxis tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} stroke="var(--border-default)" width={35} />
                                        <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} m³`, 'Spotřeba']} />
                                        <Bar dataKey="m3" fill="#3B82F6" fillOpacity={0.8} radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </>
                    ) : (
                        <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Žádné odečty za období</div>
                    )}
                </div>

                {/* Plyn */}
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 12 : 16 }}>
                    <div className="flex items-center gap-2 mb-2">
                        <Flame size={16} color="#F59E0B" />
                        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Plyn</h3>
                    </div>
                    {plyn?.denni?.length > 0 ? (
                        <>
                            <div className="flex flex-wrap gap-3 mb-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                                <span>Celkem <strong style={{ color: '#F59E0B' }}>{plyn.celkem_m3} m³</strong></span>
                                <span>Náklady <strong style={{ color: '#F59E0B' }}>{fmtKc(plyn.celkem_kc)}</strong></span>
                                <span>{plynPctTrzeb} % tržeb</span>
                            </div>
                            <div style={{ height: 160 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={plyn.denni.map(d => ({ ...d, m3: Number(d.m3), label: new Date(d.datum).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' }) }))} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} />
                                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} stroke="var(--border-default)" interval="preserveStartEnd" />
                                        <YAxis tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} stroke="var(--border-default)" width={35} />
                                        <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} m³`, 'Spotřeba']} />
                                        <Bar dataKey="m3" fill="#F59E0B" fillOpacity={0.8} radius={[3, 3, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </>
                    ) : (
                        <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Žádné odečty za období</div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// Obsah tabu Kvalita — Kontroly a měření v číslech
// ════════════════════════════════════════════════════════════════════════════

const TYP_BODU_KPI = { lednice: 'Lednice', mrazak: 'Mrazák', meridlo: 'Měřidlo', jine: 'Jiné' };
const pct = (v) => (v == null ? '—' : `${String(v).replace('.', ',')} %`);
const sklonuj = (n, jedna, dve, pet) => `${n} ${n === 1 ? jedna : n >= 2 && n <= 4 ? dve : pet}`;
const barvaPlneni = (v) => (v == null ? 'var(--text-primary)' : v >= 95 ? '#10b981' : v >= 85 ? '#f59e0b' : '#ef4444');

function KvalitaContent({ data, cardStyle, tooltipStyle, isDark, isMobile }) {
    const { souhrn, po_bodech, denni, mesice } = data;
    const graf = useMemo(() => (denni || []).map(d => ({
        ...d, label: new Date(d.datum).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' }),
    })), [denni]);
    const nepotvrzene = (mesice || []).filter(m => !m.potvrzeno);
    const hrana = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';

    const karty = [
        { label: 'Plnění měření', value: pct(souhrn.plneni_pct), color: barvaPlneni(souhrn.plneni_pct),
          sub: souhrn.plneni_mel ? `${souhrn.plneni_splnil} z ${souhrn.plneni_mel} dnů / týdnů` : 'žádný hlídaný bod' },
        { label: 'Odchylky', value: fmt(souhrn.odchylky), color: souhrn.odchylky > 0 ? '#ef4444' : '#10b981',
          sub: `${pct(souhrn.odchylky_pct)} z ${fmt(souhrn.mereni)} měření` },
        { label: 'Nezměřeno', value: fmt(souhrn.vynechano), color: souhrn.vynechano > 0 ? '#f59e0b' : '#10b981', sub: 'dnů / týdnů bez měření' },
        { label: 'Vyřízení odchylky', value: souhrn.vyrizeni_hod != null ? `${String(souhrn.vyrizeni_hod).replace('.', ',')} h` : '—',
          color: souhrn.vyrizeni_hod == null ? 'var(--text-primary)' : souhrn.vyrizeni_hod <= 24 ? '#10b981' : '#f59e0b',
          sub: `medián · ${pct(souhrn.vyrizeno_do_24h_pct)} do 24 h · otevřeno teď ${souhrn.otevrene_ted}` },
        { label: 'Checklisty dokončené', value: pct(souhrn.checklisty_pct), color: barvaPlneni(souhrn.checklisty_pct),
          sub: `${fmt(souhrn.checklisty_dokonceno)} hotových · ${fmt(souhrn.checklisty_preruseno)} přerušených` },
    ];

    return (
        <div className="space-y-6">
            {/* A. KPI karty */}
            <div style={{ display: 'grid', gap: isMobile ? 8 : 12, gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)' }}>
                {karty.map(k => (
                    <div key={k.label} className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 10 : 16 }}>
                        <div style={{ fontSize: isMobile ? 10 : 12, color: 'var(--text-secondary)', marginBottom: 4 }}>{k.label}</div>
                        <div style={{ fontSize: isMobile ? 16 : 20, fontWeight: 700, color: k.color }}>{k.value}</div>
                        <div style={{ fontSize: isMobile ? 10 : 11.5, color: 'var(--text-muted)' }}>{k.sub}</div>
                    </div>
                ))}
            </div>

            {/* B. Měsíční HACCP potvrzení */}
            {mesice?.length > 0 && (
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 12 : 16 }}>
                    <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                        Měsíční záznam měření (HACCP) {nepotvrzene.length > 0 && <span style={{ color: '#f59e0b', fontWeight: 600 }}>· nepotvrzeno {nepotvrzene.length}</span>}
                    </h3>
                    <div className="flex flex-wrap" style={{ gap: 8 }}>
                        {mesice.map(m => (
                            <span key={m.mesic} title={m.potvrzeno ? `Potvrdil(a) ${m.kdo || ''}` : 'Vedení měsíc nepotvrdilo'}
                                style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
                                    color: m.potvrzeno ? '#10b981' : '#f59e0b', border: `1px solid ${m.potvrzeno ? 'rgba(16,185,129,0.4)' : 'rgba(245,158,11,0.45)'}` }}>
                                {new Date(`${m.mesic}-01`).toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' })} · {m.potvrzeno ? 'potvrzeno' : 'nepotvrzeno'}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* C. Měření a odchylky po dnech */}
            {graf.length > 0 && (
                <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 12 : 16 }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Měření a odchylky po dnech</h3>
                    <div style={{ height: isMobile ? 200 : 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={graf} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} stroke="var(--border-default)" interval="preserveStartEnd" />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} stroke="var(--border-default)" width={32} />
                                <Tooltip contentStyle={tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="mereni" name="Měření" stackId="a" fill="#0CC0DF" fillOpacity={0.85} />
                                <Bar dataKey="odchylky" name="Odchylka" stackId="b" fill="#ef4444" fillOpacity={0.85} />
                                <Bar dataKey="vynechano" name="Nezměřeno" stackId="b" fill="#f59e0b" fillOpacity={0.85} radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* D. Po měřicích bodech */}
            <div className="rounded-xl" style={{ ...cardStyle, padding: isMobile ? 12 : 16 }}>
                <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Po měřicích bodech</h3>
                {(po_bodech || []).length === 0 && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Žádné měřicí body.</p>}
                {(po_bodech || []).map(b => (
                    <div key={b.id} className="flex items-center" style={{ gap: 10, minHeight: 40, borderBottom: `1px solid ${hrana}`, fontSize: 13 }}>
                        <div className="flex-1 min-w-0">
                            <div className="truncate" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{b.nazev}</div>
                            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                                {[TYP_BODU_KPI[b.typ], b.frekvence === 'denne' ? 'denně' : b.frekvence === 'tydne' ? 'týdně' : 'nehlídá se', `${b.mereni} měření`].join(' · ')}
                            </div>
                        </div>
                        {!isMobile && <span style={{ width: 90, textAlign: 'right', color: b.odchylky ? '#ef4444' : 'var(--text-muted)', fontSize: 12 }}>{sklonuj(b.odchylky, 'odchylka', 'odchylky', 'odchylek')}</span>}
                        {!isMobile && <span style={{ width: 90, textAlign: 'right', color: b.vynechano ? '#f59e0b' : 'var(--text-muted)', fontSize: 12 }}>{b.vynechano} nezměřeno</span>}
                        <b style={{ width: 64, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: barvaPlneni(b.plneni_pct) }}>{pct(b.plneni_pct)}</b>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// Obsah tabu Doručení
// ════════════════════════════════════════════════════════════════════════════

function DoruceniContent({ data, cardStyle, tooltipStyle, isDark, isMobile }) {
    const { souhrn, denni, po_rozvozci, po_hodinach, po_dnech } = data;

    const vcasColor = (pct) => pct >= 90 ? '#10b981' : pct >= 75 ? '#f59e0b' : '#ef4444';
    const minutColor = (m) => m <= 45 ? '#10b981' : m <= 55 ? '#f59e0b' : '#ef4444';

    const kpi = [
        { icon: Timer, label: 'Do 60 min', value: souhrn.do60_pct != null ? `${souhrn.do60_pct} %` : '—', color: souhrn.do60_pct != null ? vcasColor(souhrn.do60_pct) : '#6b7280' },
        { icon: Check, label: 'Splnění slibu', value: souhrn.slibeny_pct != null ? `${souhrn.slibeny_pct} %` : '—', color: souhrn.slibeny_pct != null ? vcasColor(souhrn.slibeny_pct - 5) : '#6b7280' },
        { icon: Truck, label: 'Ø doba doručení', value: souhrn.prumer_minut != null ? `${souhrn.prumer_minut} min` : '—', color: souhrn.prumer_minut != null ? minutColor(souhrn.prumer_minut) : '#6b7280' },
        { icon: ShoppingBag, label: 'Počet rozvozů', value: souhrn.pocet_celkem, color: '#3b82f6',
            sub: souhrn.zmena_pct != null ? `${souhrn.zmena_pct > 0 ? '+' : ''}${souhrn.zmena_pct} %` : null,
            subColor: souhrn.zmena_pct > 0 ? '#10b981' : souhrn.zmena_pct < 0 ? '#ef4444' : '#6b7280' },
    ];

    return (
        <div className="flex flex-col gap-5">
            {/* ── KPI karty ── */}
            <div className={`grid gap-3 ${isMobile ? 'grid-cols-2' : 'grid-cols-4'}`}>
                {kpi.map((k, i) => (
                    <div key={i} style={{ ...cardStyle, borderRadius: 10, padding: isMobile ? '10px 12px' : '14px 16px', display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, overflow: 'hidden' }}>
                        <div style={{ width: isMobile ? 34 : 40, height: isMobile ? 34 : 40, borderRadius: isMobile ? 10 : 12, background: `${k.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <k.icon size={isMobile ? 16 : 18} style={{ color: k.color }} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: isMobile ? 16 : 22, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.1 }}>
                                {k.value}
                                {k.sub && <span style={{ fontSize: 11, fontWeight: 500, marginLeft: 4, color: k.subColor }}>{k.sub}</span>}
                            </div>
                            <div style={{ fontSize: isMobile ? 10 : 11, color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Denní trend doby doručení ── */}
            {denni?.length > 0 && (
                <div style={{ ...cardStyle, borderRadius: 10, padding: isMobile ? 12 : 16 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>Denní trend doby doručení</h3>
                    <ResponsiveContainer width="100%" height={isMobile ? 200 : 260}>
                        <AreaChart data={denni}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'} />
                            <XAxis dataKey="datum" tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                                tickFormatter={v => { const p = v.split('-'); return `${p[2]}.${p[1]}`; }} />
                            <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} domain={[0, 'auto']} />
                            <Tooltip contentStyle={tooltipStyle}
                                formatter={(v, name) => name === 'prumer_minut' ? [`${v} min`, 'Ø doba'] : [v, name]}
                                labelFormatter={v => { const p = v.split('-'); return `${p[2]}.${p[1]}.${p[0]}`; }} />
                            <defs>
                                <linearGradient id="gradDoruceni" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <Area type="monotone" dataKey="prumer_minut" stroke="#3b82f6" fill="url(#gradDoruceni)" strokeWidth={2} dot={false} />
                            {/* Referenční čára 60 min */}
                            <Area type="monotone" dataKey={() => 60} stroke="#ef4444" strokeDasharray="5 5" strokeWidth={1} fill="none" dot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ── Rozvozci — žebříček ── */}
            {po_rozvozci?.length > 0 && (
                <div style={{ ...cardStyle, borderRadius: 10, padding: isMobile ? 12 : 16 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>
                        <Users size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
                        Rozvozci — výkon
                    </h3>
                    {/* Horizontal bar chart */}
                    <ResponsiveContainer width="100%" height={Math.max(200, po_rozvozci.length * 36)}>
                        <BarChart data={po_rozvozci} layout="vertical" margin={{ left: isMobile ? 60 : 100, right: 12 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'} />
                            <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                            <YAxis type="category" dataKey="jmeno" tick={{ fontSize: isMobile ? 10 : 11, fill: 'var(--text-secondary)' }} width={isMobile ? 55 : 95} />
                            <Tooltip contentStyle={tooltipStyle}
                                formatter={(v) => [`${v} min`, 'Ø doba']} />
                            <Bar dataKey="prumer_minut" radius={[0, 4, 4, 0]}>
                                {po_rozvozci.map((r, i) => (
                                    <Cell key={i} fill={minutColor(r.prumer_minut)} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    {/* Tabulka */}
                    <div style={{ overflowX: 'auto', marginTop: 12 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? 11 : 12, minWidth: isMobile ? 480 : 'auto' }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                    {['Rozvozce', 'Doručení', 'Ø min', '≤60 min', 'Slíbeno', 'Tržby'].map((h, i) => (
                                        <th key={i} style={{ padding: '8px 10px', textAlign: i > 0 ? 'right' : 'left', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {po_rozvozci.map((r, i) => (
                                    <tr key={i} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : '#f5f5f5'}` }}>
                                        <td style={{ padding: '8px 10px', fontWeight: 500, color: 'var(--text-primary)' }}>{r.jmeno}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-secondary)' }}>{r.pocet}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: r.prumer_minut != null ? minutColor(r.prumer_minut) : 'var(--text-muted)' }}>{r.prumer_minut ?? '—'}</td>
                                        {/* null = rozvozce měl jen časovky, do 60 min se u nich neměří */}
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: r.do60_pct != null ? vcasColor(r.do60_pct) : 'var(--text-muted)' }}>{r.do60_pct != null ? `${r.do60_pct} %` : '—'}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: r.slibeny_pct != null ? vcasColor(r.slibeny_pct - 5) : 'var(--text-muted)' }}>{r.slibeny_pct != null ? `${r.slibeny_pct} %` : '—'}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-secondary)' }}>{Math.round(r.trzby).toLocaleString('cs')} Kč</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── Výkon dle hodiny ── */}
            {po_hodinach?.length > 0 && (
                <div style={{ ...cardStyle, borderRadius: 10, padding: isMobile ? 12 : 16 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>Výkon dle hodiny</h3>
                    <ResponsiveContainer width="100%" height={isMobile ? 180 : 220}>
                        <BarChart data={po_hodinach}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'} />
                            <XAxis dataKey="hodina" tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                                tickFormatter={v => `${v}:00`} />
                            <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                            <Tooltip contentStyle={tooltipStyle}
                                formatter={(v, name) => name === 'prumer_minut' ? [`${v} min`, 'Ø doba'] : [`${v}`, 'Objednávek']}
                                labelFormatter={v => `${v}:00–${v}:59`} />
                            <Bar dataKey="prumer_minut" radius={[4, 4, 0, 0]} name="prumer_minut">
                                {po_hodinach.map((h, i) => (
                                    <Cell key={i} fill={minutColor(h.prumer_minut)} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ── Heat mapa: den × hodina (doručení) ── */}
            {data.heat_mapa?.length > 0 && (() => {
                const matrix = {};
                let maxPocet = 0;
                const hodiny = [];
                data.heat_mapa.forEach(c => {
                    if (!matrix[c.den]) matrix[c.den] = {};
                    matrix[c.den][c.hodina] = c;
                    if (c.pocet > maxPocet) maxPocet = c.pocet;
                    if (!hodiny.includes(c.hodina)) hodiny.push(c.hodina);
                });
                hodiny.sort((a, b) => a - b);

                return (
                    <div style={{ ...cardStyle, borderRadius: 10, padding: isMobile ? 12 : 16 }}>
                        <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>Doba doručení — den × hodina</h3>
                        <div style={{ overflowX: 'auto' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: `48px repeat(${hodiny.length}, 1fr)`, gap: 2, minWidth: hodiny.length * 48 + 48 }}>
                                <div />
                                {hodiny.map(h => (
                                    <div key={h} style={{ textAlign: 'center', fontSize: 10, fontWeight: 500, padding: '4px 0', color: 'var(--text-secondary)' }}>{h}h</div>
                                ))}
                                {DNY_LABELS.map((den, di) => (
                                    <div key={di} style={{ display: 'contents' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)' }}>{den}</div>
                                        {hodiny.map(h => {
                                            const cell = matrix[di]?.[h];
                                            if (!cell) return <div key={`${di}-${h}`} style={{ minHeight: 32, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }} />;
                                            const color = cell.prumer_minut <= 45 ? '#10b981' : cell.prumer_minut <= 60 ? '#f59e0b' : '#ef4444';
                                            const intensity = Math.min(1, cell.pocet / Math.max(maxPocet, 1));
                                            return (
                                                <div key={`${di}-${h}`}
                                                    title={`${den} ${h}:00 — ${cell.pocet} obj, Ø ${cell.prumer_minut} min, ${cell.do60_pct}% ≤60`}
                                                    style={{
                                                        minHeight: 32, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        background: `${color}${Math.round((intensity * 0.6 + 0.15) * 255).toString(16).padStart(2, '0')}`,
                                                        color: intensity > 0.3 ? '#fff' : 'var(--text-secondary)',
                                                        fontSize: 10, fontWeight: 600,
                                                    }}>
                                                    {Math.round(cell.prumer_minut)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 10, color: 'var(--text-muted)' }}>
                            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#10b981', marginRight: 4, verticalAlign: '-1px' }} />≤45 min</span>
                            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#f59e0b', marginRight: 4, verticalAlign: '-1px' }} />46–60 min</span>
                            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#ef4444', marginRight: 4, verticalAlign: '-1px' }} />&gt;60 min</span>
                        </div>
                    </div>
                );
            })()}

            {/* ── Výkon dle dne v týdnu ── */}
            {po_dnech?.length > 0 && (
                <div style={{ ...cardStyle, borderRadius: 10, padding: isMobile ? 12 : 16 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>Výkon dle dne v týdnu</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : 'repeat(7, 1fr)', gap: isMobile ? 6 : 8 }}>
                        {po_dnech.map((d, i) => {
                            const bgColor = d.pocet > 0 ? `${vcasColor(d.do60_pct)}12` : 'transparent';
                            const borderClr = d.pocet > 0 ? `${vcasColor(d.do60_pct)}30` : (isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)');
                            return (
                                <div key={i} style={{
                                    borderRadius: 7, padding: '10px 8px', textAlign: 'center',
                                    background: bgColor, border: `1px solid ${borderClr}`,
                                }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{d.den}</div>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: d.prumer_minut != null ? minutColor(d.prumer_minut) : 'var(--text-muted)' }}>
                                        {d.prumer_minut != null ? `${d.prumer_minut}` : '—'}
                                    </div>
                                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>min</div>
                                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>{d.pocet} obj.</div>
                                    {d.do60_pct != null && (
                                        <div style={{ fontSize: 11, fontWeight: 600, color: vcasColor(d.do60_pct), marginTop: 2 }}>{d.do60_pct} %</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Mapa lokací + tabulka měst ── */}
            {(data.mapa_lokaci?.length > 0 || data.po_mestech?.length > 0) && (
                <div style={{ ...cardStyle, borderRadius: 10, padding: isMobile ? 12 : 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <MapPin size={16} style={{ color: '#3b82f6' }} />
                        <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Včasnost dle lokace</h3>
                        {data.mapa_lokaci?.length > 0 && (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{data.mapa_lokaci.length} lokací</span>
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 16 }}>
                        {/* Mapa */}
                        {data.mapa_lokaci?.length > 0 && (
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ borderRadius: 9, overflow: 'hidden', height: isMobile ? 260 : 400 }}>
                                    <MapContainer center={[49.5483, 18.2108]} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                                        <TileLayer
                                            url={isDark
                                                ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                                                : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
                                            attribution='&copy; OSM'
                                        />
                                        {data.mapa_lokaci.map((lok, i) => {
                                            const prumer = lok.prumer_minut;
                                            const color = prumer == null ? '#6b7280' : prumer <= 60 ? '#10b981' : prumer <= 75 ? '#f59e0b' : '#ef4444';
                                            const maxPocet = data.mapa_lokaci[0]?.pocet || 1;
                                            const radius = Math.max(4, Math.min(16, (lok.pocet / maxPocet) * 16));
                                            return (
                                                <CircleMarker key={i} center={[Number(lok.lat), Number(lok.lng)]} radius={radius}
                                                    pathOptions={{ color, fillColor: color, fillOpacity: 0.5, weight: 2 }}>
                                                    <Popup>
                                                        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                                                            <strong>{lok.adresa}</strong><br />
                                                            {lok.mesto}<br />
                                                            {lok.pocet}× doručeno<br />
                                                            {prumer != null && <><span style={{ color, fontWeight: 600 }}>Ø {prumer} min</span> · {lok.do60_pct}% ≤60 min</>}
                                                        </div>
                                                    </Popup>
                                                </CircleMarker>
                                            );
                                        })}
                                    </MapContainer>
                                </div>
                                <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 10, color: 'var(--text-muted)' }}>
                                    <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 99, background: '#10b981', marginRight: 4, verticalAlign: '-1px' }} />≤60 min</span>
                                    <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 99, background: '#f59e0b', marginRight: 4, verticalAlign: '-1px' }} />61–75 min</span>
                                    <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 99, background: '#ef4444', marginRight: 4, verticalAlign: '-1px' }} />&gt;75 min</span>
                                </div>
                            </div>
                        )}
                        {/* Tabulka měst */}
                        {data.po_mestech?.length > 0 && (
                            <div style={{ width: isMobile ? '100%' : undefined, maxHeight: isMobile ? 200 : 430, overflowY: 'auto' }} className={isMobile ? '' : 'lg:w-80 xl:w-96'}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? 11 : 12 }}>
                                    <thead className="sticky top-0" style={{ background: isDark ? '#141416' : 'var(--surface-raised)' }}>
                                        <tr style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                            {['Město', 'Obj.', 'Ø min', '≤60'].map((h, i) => (
                                                <th key={i} style={{ padding: '8px 8px', textAlign: i > 0 ? 'right' : 'left', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.po_mestech.map((m, i) => {
                                            const c = m.prumer_minut <= 60 ? '#10b981' : m.prumer_minut <= 75 ? '#f59e0b' : '#ef4444';
                                            return (
                                                <tr key={i} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : '#f5f5f5'}` }}>
                                                    <td style={{ padding: '7px 8px', fontWeight: 500, color: 'var(--text-primary)' }}>{m.mesto}</td>
                                                    <td style={{ padding: '7px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>{m.pocet}</td>
                                                    <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 600, color: c }}>{m.prumer_minut}</td>
                                                    <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 600, color: vcasColor(m.do60_pct) }}>{m.do60_pct}%</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Vliv počasí ── */}
            {denni?.length > 5 && (() => {
                const dny = denni.filter(d => d.pocet > 0);
                const dest = dny.filter(d => Number(d.srazky_mm) > 1);
                const sucho = dny.filter(d => d.srazky_mm != null && Number(d.srazky_mm) <= 1);
                const avgDest = dest.length ? Math.round(dest.reduce((s, d) => s + d.prumer_minut, 0) / dest.length * 10) / 10 : null;
                const avgSucho = sucho.length ? Math.round(sucho.reduce((s, d) => s + d.prumer_minut, 0) / sucho.length * 10) / 10 : null;

                const bands = [
                    { label: '< 5 °C', min: -50, max: 5 },
                    { label: '5–15 °C', min: 5, max: 15 },
                    { label: '15–25 °C', min: 15, max: 25 },
                    { label: '> 25 °C', min: 25, max: 60 },
                ];
                const tempData = bands.map(b => {
                    const rows = dny.filter(d => d.teplota_prumer != null && d.teplota_prumer >= b.min && d.teplota_prumer < b.max);
                    return {
                        label: b.label,
                        prumer: rows.length ? Math.round(rows.reduce((s, d) => s + d.prumer_minut, 0) / rows.length * 10) / 10 : null,
                        dnu: rows.length,
                    };
                });

                if (!avgDest && !avgSucho) return null;

                return (
                    <div style={{ ...cardStyle, borderRadius: 10, padding: isMobile ? 12 : 16 }}>
                        <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>Vliv počasí na doručení</h3>
                        <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-3 mb-3`}>
                            <div style={{ borderRadius: 7, padding: 14, background: isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`, textAlign: 'center' }}>
                                <CloudRain size={20} style={{ color: '#3b82f6', margin: '0 auto 6px' }} />
                                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Déšť (&gt;1 mm)</div>
                                <div style={{ fontSize: 20, fontWeight: 700, color: avgDest ? minutColor(avgDest) : 'var(--text-muted)' }}>{avgDest ?? '—'} min</div>
                                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{dest.length} dní</div>
                            </div>
                            <div style={{ borderRadius: 7, padding: 14, background: isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`, textAlign: 'center' }}>
                                <Sun size={20} style={{ color: '#f59e0b', margin: '0 auto 6px' }} />
                                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Sucho (≤1 mm)</div>
                                <div style={{ fontSize: 20, fontWeight: 700, color: avgSucho ? minutColor(avgSucho) : 'var(--text-muted)' }}>{avgSucho ?? '—'} min</div>
                                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{sucho.length} dní</div>
                            </div>
                        </div>
                        <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-4'} gap-2`}>
                            {tempData.map((t, i) => (
                                <div key={i} style={{ borderRadius: 7, padding: '10px 8px', textAlign: 'center', background: isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}` }}>
                                    <Thermometer size={14} style={{ color: 'var(--text-muted)', margin: '0 auto 4px' }} />
                                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{t.label}</div>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: t.prumer != null ? minutColor(t.prumer) : 'var(--text-muted)', marginTop: 2 }}>{t.prumer ?? '—'}</div>
                                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{t.dnu} dní</div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}
